import { create } from 'zustand';
import { FeatureFlag } from '../types';
import ConfigurationService from '../services/ConfigurationService';
import { featureFlagApi } from '../services/api/featureFlagApi';
import logger from '../services/loggerService';

interface FeatureFlagState {
  flags: Record<string, boolean>;
  allFlags: FeatureFlag[];
  isLoading: boolean;
  fetchFlags: (signal?: AbortSignal) => Promise<void>;
  updateFlag: (key: string, isEnabled: boolean) => Promise<void>;
  isEnabled: (key: string) => boolean;
}

export const useFeatureFlagStore = create<FeatureFlagState>((set, get) => ({
  flags: {},
  allFlags: [],
  isLoading: true,

  fetchFlags: async (signal?: AbortSignal) => {
    set({ isLoading: true });
    try {
      const flagsFromServer = await featureFlagApi.getFeatureFlags(signal);
      const flagMap = flagsFromServer.reduce(
        (acc, flag) => {
          acc[flag.key] = flag.isEnabled;
          return acc;
        },
        {} as Record<string, boolean>
      );

      set({ flags: flagMap, allFlags: flagsFromServer, isLoading: false });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        logger.debug('Fetch feature flags aborted');
        return;
      }
      logger.error('Failed to fetch feature flags', error, 'useFeatureFlagStore.fetchFlags');
      // Use ConfigurationService for fallback defaults instead of hardcoded values
      const defaultFlags = ConfigurationService.getDefaultFeatureFlags();
      set({
        isLoading: false,
        flags: defaultFlags,
      });
    }
  },

  updateFlag: async (key: string, isEnabled: boolean) => {
    try {
      await featureFlagApi.updateFeatureFlag(key, isEnabled);
      // Optimistically update the local state
      set((state) => ({
        flags: { ...state.flags, [key]: isEnabled },
        allFlags: state.allFlags.map((f) => (f.key === key ? { ...f, isEnabled } : f)),
      }));
    } catch (error) {
      logger.error(`Failed to update feature flag ${key}`, error, 'useFeatureFlagStore.updateFlag');
      // Re-throw to be handled by the component
      throw error;
    }
  },

  isEnabled: (key: string) => {
    return get().flags[key] ?? false; // Default to false if a flag is not found
  },
}));
